import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { IconButton, Slider, Stack, Typography } from '@mui/material';
import { type FC, useEffect, useState } from 'react';

import { type VisionReplayFrame } from '../../api/index.js';

interface Props {
  frames: VisionReplayFrame[];
  selectedIndex: number;
  sampleFps: number;
  onSelect: (index: number) => void;
}

function formatSeconds(timestampMs: number): string {
  return `${(timestampMs / 1000).toFixed(1)} s`;
}

const VisionReplayScrubber: FC<Props> = ({ frames, selectedIndex, sampleFps, onSelect }) => {
  const [playing, setPlaying] = useState(false);
  const lastIndex = Math.max(frames.length - 1, 0);
  const index = Math.min(selectedIndex, lastIndex);

  useEffect(() => {
    if (!playing) {
      return undefined;
    }
    if (index >= lastIndex) {
      setPlaying(false);
      return undefined;
    }
    const timer = window.setTimeout(() => onSelect(index + 1), 1000 / sampleFps);
    return () => window.clearTimeout(timer);
  }, [playing, index, lastIndex, sampleFps, onSelect]);

  const togglePlaying = () => {
    if (!playing && index >= lastIndex) {
      onSelect(0);
    }
    setPlaying(!playing);
  };

  return (
    <Stack direction={'row'} alignItems={'center'} spacing={1}>
      <IconButton size={'small'} disabled={index === 0} onClick={() => onSelect(index - 1)} aria-label={'Previous frame'}>
        <NavigateBeforeIcon />
      </IconButton>
      <IconButton size={'small'} disabled={frames.length < 2} onClick={togglePlaying} aria-label={playing ? 'Pause' : 'Play'}>
        {playing ? <PauseIcon /> : <PlayArrowIcon />}
      </IconButton>
      <IconButton size={'small'} disabled={index >= lastIndex} onClick={() => onSelect(index + 1)} aria-label={'Next frame'}>
        <NavigateNextIcon />
      </IconButton>
      <Slider
        size={'small'}
        min={0}
        max={lastIndex}
        step={1}
        value={index}
        disabled={frames.length < 2}
        onChange={(_event, next) => onSelect(next as number)}
        sx={{ flex: 1, mx: 1 }}
      />
      <Typography variant={'caption'} color={'text.secondary'} sx={{ minWidth: 88, textAlign: 'right' }}>
        {frames.length > 0 ? `${index + 1}/${frames.length} · ${formatSeconds(frames[index]?.timestampMs ?? 0)}` : '—'}
      </Typography>
    </Stack>
  );
};

export default VisionReplayScrubber;
